import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from './Icon';
import useAuthStore from '../store/authStore';
import { useSmartTranslation } from '../hooks/useStudentLabel';

/**
 * Bottom Navigation Bar for Mobile
 * Enterprise-grade bottom nav with active indicator pill,
 * bolder icons, and smooth transitions.
 */
export default function BottomNav({ onMenuClick }) {
  const { i18n } = useTranslation();
  const t = useSmartTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, centerType, learningSubType } = useAuthStore();

  const getBottomNavItems = () => {
    if (user?.role === 'teacher') {
      if (centerType === 'hair_transplant') {
        return [
          { labelKey: 'sidebar.workerDashboard', iconName: 'dashboard', path: '/worker/dashboard' },
          { labelKey: 'sidebar.calendar', iconName: 'event', path: '/calendar' },
          { labelKey: 'bulkWhatsApp.sidebarLabel', iconName: 'campaign', path: '/whatsapp/bulk-send' },
          { labelKey: 'common.menu', iconName: 'menu', path: '/menu' }
        ];
      }
      return [
        { labelKey: 'sidebar.dashboard', iconName: 'dashboard', path: '/teacher/dashboard' },
        // The tab caption, not the page heading: 'groups.checklists' is
        // "Assigned Checklists", which no longer fits a fifth of a phone.
        { labelKey: 'sidebar.checklists', iconName: 'checklist', path: '/teacher/checklists' },
        { labelKey: 'groups.attendance', iconName: 'event_note', path: '/teacher/attendance' },
        { labelKey: 'common.menu', iconName: 'menu', path: '/menu' }
      ];
    }

    if (centerType === 'therapy') {
      return [
        { labelKey: 'sidebar.dashboard', iconName: 'home', path: '/dashboard' },
        { labelKey: 'sidebar.calendar', iconName: 'event', path: '/calendar' },
        { labelKey: 'sidebar.students', iconName: 'person', path: '/students' },
        { labelKey: 'survey.title', iconName: 'construction', path: '/surveys' },
        { labelKey: 'common.menu', iconName: 'menu', path: '/menu' }
      ];
    }

    if (centerType === 'hair_transplant') {
      return [
        { labelKey: 'sidebar.dashboard', iconName: 'home', path: '/dashboard' },
        // Single "Clients" (לקוחות) tab — see Sidebar.jsx. The old "Patients"
        // (מטופלים) /patients tab was the same data under a different name.
        { labelKey: 'sidebar.students', iconName: 'person', path: '/students' },
        { labelKey: 'sidebar.calendar', iconName: 'event', path: '/calendar' },
        { labelKey: 'common.menu', iconName: 'menu', path: '/menu' }
      ];
    }

    if (centerType === 'pharma_clinic') {
      return [
        { labelKey: 'sidebar.students', iconName: 'person', path: '/students' },
        { labelKey: 'orders.title', iconName: 'receipt_long', path: '/orders' },
        { labelKey: 'common.menu', iconName: 'menu', path: '/menu' }
      ];
    }

    // Sport sub-type: its trimmed menu has no Calendar, so the bar carries
    // the pages a manager opens most — Sessions, Clients, Groups.
    if (centerType === 'learning' && learningSubType === 'sport') {
      return [
        { labelKey: 'sidebar.dashboard', iconName: 'home', path: '/dashboard' },
        // "Training sessions" is too long for a tab: the bar says "Training".
        { labelKey: 'bottomNav.sportSessions', iconName: 'sports', path: '/lessons' },
        { labelKey: 'sidebar.students', iconName: 'person', path: '/students' },
        { labelKey: 'sidebar.groups', iconName: 'groups', path: '/groups' },
        { labelKey: 'common.menu', iconName: 'menu', path: '/menu' }
      ];
    }

    // Default education center
    return [
      { labelKey: 'sidebar.dashboard', iconName: 'home', path: '/dashboard' },
      { labelKey: 'sidebar.calendar', iconName: 'event', path: '/calendar' },
      { labelKey: 'sidebar.students', iconName: 'person', path: '/students' },
      { labelKey: 'sidebar.groups', iconName: 'class', path: '/groups' },
      { labelKey: 'common.menu', iconName: 'menu', path: '/menu' }
    ];
  };

  const navItems = getBottomNavItems();

  const matchesTab = (path) =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  const isActive = (path) => {
    if (path === '/menu') {
      return !navItems.some((i) => i.path !== '/menu' && matchesTab(i.path));
    }
    return matchesTab(path);
  };

  const handleNavClick = (path) => {
    if (path === '/menu') {
      if (onMenuClick) return onMenuClick();
      navigate('/settings');
    } else {
      navigate(path);
    }
  };

  return (
    <nav className="bottom-nav">
      {navItems.map((item, index) => {
        const active = isActive(item.path);
        return (
          <button
            key={index}
            className={`bottom-nav-item ${active ? 'active' : ''}`}
            onClick={() => handleNavClick(item.path)}
            aria-label={t(item.labelKey)}
            aria-current={active ? 'page' : undefined}
          >
            <div className={`bottom-nav-icon-wrap ${active ? 'active' : ''}`}>
              <Icon name={item.iconName} className="icon" />
            </div>
            <span className="label">{t(item.labelKey)}</span>
          </button>
        );
      })}
    </nav>
  );
}
